import React from "react";
import {
  Container,
  Tab,
  Tabs,
  Typography,
  MenuItem,
  Menu
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/AutoAwesomeMosaicSharp";
import RunsIcon from "@mui/icons-material/DirectionsRun";
import { ReactComponent as MachineIcon } from "assets/icons/machine.svg";
import SoftwareIcon from "@mui/icons-material/Update";
import SettingsIcon from "@mui/icons-material/Settings";
import { Link, useLocation } from "react-router-dom";
import NavbarProps from "./Navbar.types";
import Loading from "common/ui/Loading";
import "./Navbar.scss";
import { useTranslation } from "react-i18next";
import useHasPermissions from "hooks/useHasPermissions";
import Permission from "types/Permission.enum";
import { ErrorBoundary } from "react-error-boundary";
import Error from "common/ui/Error";
import DashedDivider from "common/ui/DashedDivider";
import ModalProvider from "common/core/ModalProvider";

function Navbar(props: NavbarProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const inferredTab = location.pathname.split("/")[1] || "dashboard";

  const hasSettingsPermission = useHasPermissions(
    [Permission.VIEW_ALL_USERS, Permission.VIEW_ALL_AUDIT_LOGS],
    true
  );
  const hasMachinesPermission = useHasPermissions([
    Permission.VIEW_ALL_MACHINES
  ]);
  const hasAuditLogsPermission = useHasPermissions([
    Permission.VIEW_ALL_AUDIT_LOGS
  ]);
  const hasUserManagementPermission = useHasPermissions([
    Permission.VIEW_ALL_USERS
  ]);

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <div className="navbar">
        <Tabs
          component={Container}
          maxWidth="xl"
          disableGutters
          value={inferredTab}
          classes={{ indicator: "navbar__indicator-arrow" }}
        >
          <Tab
            classes={{ root: "navbar__tab" }}
            component={Link}
            to="/dashboard"
            label={
              <Typography color="tertiary.main" variant="h6">
                {t("navbar.dashboard")}
              </Typography>
            }
            icon={<DashboardIcon color="tertiary" />}
            value="dashboard"
          />
          <Tab
            classes={{ root: "navbar__tab" }}
            component={Link}
            to="/runs"
            label={
              <Typography color="tertiary.main" variant="h6">
                {t("navbar.runs")}
              </Typography>
            }
            icon={<RunsIcon color="tertiary" />}
            value="runs"
          />
          <Tab
            sx={{ display: hasMachinesPermission ? "flex" : "none" }}
            classes={{ root: "navbar__tab" }}
            component={Link}
            to="/machines"
            label={
              <Typography color="tertiary.main" variant="h6">
                {t("navbar.machines")}
              </Typography>
            }
            icon={<MachineIcon />}
            value="machines"
          />
          <Tab
            classes={{ root: "navbar__tab" }}
            component={Link}
            to="/softwares"
            label={
              <Typography color="tertiary.main" variant="h6">
                {t("navbar.software")}
              </Typography>
            }
            icon={<SoftwareIcon color="tertiary" />}
            value="softwares"
          />
          <Tab
            sx={{ display: hasSettingsPermission ? "flex" : "none" }}
            classes={{ root: "navbar__tab" }}
            label={
              <Typography color="tertiary.main" variant="h6">
                {t("navbar.settings")}
              </Typography>
            }
            icon={<SettingsIcon color="tertiary" />}
            value="settings"
            onClick={handleOpenMenu}
          />
        </Tabs>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
        >
          <MenuItem
            onClick={() => {
              handleCloseMenu();
            }}
            component={Link}
            to="/settings/user-management"
            sx={{
              width: 200,
              display: hasUserManagementPermission ? "flex" : "none"
            }}
          >
            <Typography
              variant="subtitle1"
              color="primary.dark"
              fontWeight="medium"
            >
              {t("navbar.userManagement")}
            </Typography>
          </MenuItem>

          <DashedDivider
            style={{
              width: "93%",
              display:
                hasAuditLogsPermission && hasUserManagementPermission
                  ? "flex"
                  : "none"
            }}
          />
          <MenuItem
            onClick={() => {
              handleCloseMenu();
            }}
            component={Link}
            to="/settings/audit-logs"
            sx={{
              width: 200,
              display: hasAuditLogsPermission ? "flex" : "none"
            }}
          >
            <Typography
              variant="subtitle1"
              color="primary.dark"
              fontWeight="medium"
            >
              {t("navbar.auditLogs")}
            </Typography>
          </MenuItem>
        </Menu>
      </div>
      <ErrorBoundary fallback={<Error />} resetKeys={[location.pathname]}>
        <React.Suspense fallback={<Loading />}>
          {props.children}
          <ModalProvider />
        </React.Suspense>
      </ErrorBoundary>
    </>
  );
}

export default Navbar;
